import { useState } from 'react';
import type { UploadedMedia } from './MediaUpload';

/**
 * MIME type for drag-and-drop data transfer.
 * Custom type to identify our media items during drag operations.
 */
export const MEDIA_DRAG_TYPE = 'application/x-hex-media';

/**
 * Props for the MediaLibrary component.
 */
interface MediaLibraryProps {
    /** List of media items to display */
    media: UploadedMedia[];
    /** Base URL for delete operations (e.g., `/hex-media/1` or `/character/1/media`) */
    deleteUrlBase: string;
    /** Callback when a media item is deleted */
    onDelete?: (mediaId: number) => void;
    /** Whether the user can delete media */
    canDelete?: boolean;
    /** Whether drag-and-drop is enabled for inserting images */
    draggable?: boolean;
}

/**
 * MediaLibrary displays a grid of uploaded images with actions.
 * Supports copying markdown links and deleting images.
 */
export function MediaLibrary({
    media,
    deleteUrlBase,
    onDelete,
    canDelete = true,
    draggable = true,
}: MediaLibraryProps) {
    const [copiedId, setCopiedId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [draggingId, setDraggingId] = useState<number | null>(null);

    /**
     * Handle drag start - serialize media data for transfer.
     */
    const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: UploadedMedia) => {
        if (!draggable) return;

        // Set the drag data with our custom MIME type
        e.dataTransfer.setData(MEDIA_DRAG_TYPE, JSON.stringify(item));
        e.dataTransfer.setData('text/plain', `![${item.alt_text || item.original_filename}](${item.urls.medium})`);
        e.dataTransfer.effectAllowed = 'copy';

        // Set a custom drag image (the thumbnail)
        const img = e.currentTarget.querySelector('img');
        if (img) {
            e.dataTransfer.setDragImage(img, 40, 40);
        }

        setDraggingId(item.id);
    };

    /**
     * Handle drag end - reset dragging state.
     */
    const handleDragEnd = () => {
        setDraggingId(null);
    };

    /**
     * Copy markdown link to clipboard.
     */
    const copyMarkdownLink = async (item: UploadedMedia) => {
        // Use medium size for markdown embeds
        const markdown = `![${item.alt_text || item.original_filename}](${item.urls.medium})`;

        try {
            await navigator.clipboard.writeText(markdown);
            setCopiedId(item.id);
            // Reset after 2 seconds
            setTimeout(() => setCopiedId(null), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    /**
     * Delete a media item.
     */
    const handleDelete = async (item: UploadedMedia) => {
        if (!confirm(`Delete "${item.original_filename}"? This cannot be undone.`)) {
            return;
        }

        setDeletingId(item.id);

        try {
            // Get CSRF token from cookie
            const csrfToken = document.cookie
                .split('; ')
                .find(row => row.startsWith('XSRF-TOKEN='))
                ?.split('=')[1];

            const response = await fetch(`${deleteUrlBase}/${item.id}`, {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'X-XSRF-TOKEN': csrfToken ? decodeURIComponent(csrfToken) : '',
                },
            });

            if (response.ok) {
                onDelete?.(item.id);
            } else {
                const data = await response.json();
                alert(data.message || 'Failed to delete');
            }
        } catch (err) {
            console.error('Delete failed:', err);
            alert('Failed to delete image');
        } finally {
            setDeletingId(null);
        }
    };

    if (media.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p className="text-sm">No images uploaded yet</p>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-2 gap-2">
            {media.map((item) => (
                <div
                    key={item.id}
                    draggable={draggable}
                    onDragStart={(e) => handleDragStart(e, item)}
                    onDragEnd={handleDragEnd}
                    className={`relative group rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 ${
                        draggable ? 'cursor-grab active:cursor-grabbing' : ''
                    } ${draggingId === item.id ? 'opacity-50 ring-2 ring-purple-500' : ''}`}
                >
                    {/* Thumbnail image */}
                    <img
                        src={item.urls.small}
                        alt={item.alt_text || item.original_filename}
                        className="w-full h-24 object-cover pointer-events-none"
                        loading="lazy"
                    />

                    {/* Drag hint overlay (shown when not hovering) */}
                    {draggable && (
                        <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <span className="text-xs bg-purple-600/80 text-white px-1.5 py-0.5 rounded">
                                Drag to editor
                            </span>
                        </div>
                    )}

                    {/* Overlay with actions */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1 p-1">
                        {/* Filename */}
                        <span className="text-xs text-white truncate max-w-full px-1">
                            {item.original_filename}
                        </span>

                        {/* Action buttons */}
                        <div className="flex gap-1">
                            {/* Copy markdown link button */}
                            <button
                                onClick={() => copyMarkdownLink(item)}
                                className="px-2 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                                title="Copy markdown link"
                            >
                                {copiedId === item.id ? '✓ Copied' : 'Copy Link'}
                            </button>

                            {/* Delete button */}
                            {canDelete && (
                                <button
                                    onClick={() => handleDelete(item)}
                                    disabled={deletingId === item.id}
                                    className="px-2 py-1 text-xs bg-red-600 hover:bg-red-700 text-white rounded transition-colors disabled:opacity-50"
                                    title="Delete image"
                                >
                                    {deletingId === item.id ? '...' : 'Delete'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default MediaLibrary;

